/* eslint-disable @typescript-eslint/return-await */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { getAccountInfo } from 'hooks/account/useAccountInfo';

import teamService from '../services/teamService';
import { getPermissionLevelInTeam, updatedUserPermissionLevel } from '../utils/permissionHelpers';
import { createTeam, editTeam, inviteUser, referUser } from './actions/teamActions';

export const getAllInvites = createAsyncThunk('team/getAllInvites', async () => {
  const allInvites = await teamService.getAllInvites();

  return allInvites;
});

export const deleteInvites = createAsyncThunk('team/deleteInvite', async ({ inviteCodes }) => {
  await teamService.deleteInvites(inviteCodes);
  return inviteCodes;
});

export const deleteUsersFromOrganization = createAsyncThunk(
  'team/deleteUsersFromOrganization',
  async ({ userIds, organizationId }, { rejectWithValue }) => {
    try {
      await teamService.changeUserPermission(userIds, organizationId, 'none');
      return { userIds, organizationId };
    } catch (err) {
      return rejectWithValue(typeof err === 'string' ? err : err?.detail);
    }
  }
);

export const changeUserPermissionLevel = createAsyncThunk(
  'team/changeUserPermissionLevel',
  async ({ user, organizationId, permissionLevel, fromProUpgrade = false }, { rejectWithValue }) => {
    try {
      const [changedUser] = await teamService.changeUserPermission([user.id], organizationId, permissionLevel, fromProUpgrade);

      return changedUser;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getTeamInvites = createAsyncThunk('team/getTeamInvites', async ({ id, emailParam, sortParam }) => {
  return await teamService.getTeamInvites(id, emailParam, sortParam);
});

export const getTeamUsers = createAsyncThunk(
  'team/getTeamUsers',
  async ({ id, nameParam, sortParam, annotateCountInfo, annotateViewCountInfo, role, limit }) => {
    const teammates = await teamService.getTeamUsers(id, nameParam, sortParam, annotateCountInfo, annotateViewCountInfo, role, limit);

    return { teammates, organizationId: id };
  }
);

export const getNextTeamUsers = createAsyncThunk('team/getNextTeamUsers', async ({ url, annotateCountInfo, annotateViewCountInfo }) => {
  return await teamService.getNextTeamUsers(url, annotateCountInfo, annotateViewCountInfo);
});

export const getJoinableTeamUsers = createAsyncThunk('team/getJoinableTeamUsers', async (id) => {
  return await teamService.getJoinableTeamUsers(id);
});

export const deleteTeam = createAsyncThunk('team/deleteTeam', async ({ id }) => {
  await teamService.deleteTeam(id);
  return id;
});

export const acceptInvite = createAsyncThunk('team/acceptInvite', async ({ inviteCode }, { rejectWithValue }) => {
  try {
    return await teamService.acceptInvite(inviteCode);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const rejectInvite = createAsyncThunk('team/rejectInvite', async ({ inviteCode }) => {
  return await teamService.rejectInvite(inviteCode);
});

export const acknowledgeInvite = createAsyncThunk('team/acknowledgeInvite', async ({ inviteCode }) => {
  return await teamService.acknowledgeInvite(inviteCode);
});

export const leaveTeam = createAsyncThunk('team/leaveTeam', async (id) => {
  return await teamService.leaveTeam(id);
});

export const updateSetting = createAsyncThunk(
  'team/updateSetting',
  async ({ teamId, setting, settingValue, isSuperOrganizationSetting = false }) => {
    return await teamService.updateSetting(teamId, setting, settingValue, isSuperOrganizationSetting);
  }
);

export const requestAccess = createAsyncThunk('team/requestAccess', async (id) => {
  return await teamService.requestAccess(id);
});

export const listAccessRequests = createAsyncThunk('team/listAccessRequests', async (id) => {
  return await teamService.listAccessRequests(id);
});

export const acceptRequest = createAsyncThunk('team/acceptRequest', async ({ id, email, permissionLevel }) => {
  return await teamService.acceptRequest(id, email, permissionLevel);
});

export const rejectRequest = createAsyncThunk('team/rejectRequest', async ({ id, email }) => {
  return await teamService.rejectRequest(id, email);
});

export const processInviteLinkSignup = createAsyncThunk('team/processInviteLinkSignup', async ({ invitingUserId, organizationId }) => {
  await teamService.processInviteLinkSignup(invitingUserId, organizationId);
});

export const resendInvite = createAsyncThunk('team/resendInvite', async ({ inviteCode }) => {
  return teamService.resendInvite(inviteCode);
});

export const bulkResendInvites = createAsyncThunk('team/bulkResendInvites', async ({ inviteCodes }) => {
  return teamService.bulkResendInvites(inviteCodes);
});

export const resendAllInvitesForTeam = createAsyncThunk('team/resendAllInvitesForTeam', async ({ teamId }) => {
  return teamService.resendAllInvitesForTeam(teamId);
});

export const getAdditionalTeamInfo = createAsyncThunk('team/getAdditionalTeamInfo', async (teamId) => {
  return teamService.getAdditionalTeamInfo(teamId);
});

export const listOrganizationSettings = createAsyncThunk('team/listOrganizationSettings', async (teamId) => {
  return teamService.listOrganizationSettings(teamId);
});

export const INITIAL_STATE = {
  teammates: [],
  organizationId: null,
  totalNumOfUsers: 0,
  numOfUsersExcludingScribe: 0,
  outsideRequests: [],
  userRequests: [],
  invites: [], // invited to user's team -> TeamSettings
  allInvites: [], // all invites from other teams -> TeamDropdown
  referrals: [],
  teams: [],
  activeTeam: null,
  isError: false,
  errorMessage: null,
  isLoading: false,
  isLoadingNext: false,
  loadingTeamInvites: false,
  selectedTeamId: undefined,
  accessLevel: null,
  teamLogo: null,
  teamUrl: null,
  defaultCommentEnabledForEditor: false,
  defaultCommentEnabledForViewer: false,
  showUpdateReminder: false,
  updateReminderInterval: null,
  showMadeWithScribe: false,
  nextUrl: null,
  previousUrl: null
};

export const teamSlice = createSlice({
  name: 'team',
  initialState: { ...INITIAL_STATE },
  reducers: {
    setActiveTeam: (state, action) => {
      state.activeTeam = action.payload.team;
    },
    setSelectedTeamId: (state, action) => {
      state.selectedTeamId = action.payload;
      state.teammates = [];
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllInvites.fulfilled, (state, action) => {
        state.allInvites = action.payload.invites;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAllInvites.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAllInvites.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getAdditionalTeamInfo.fulfilled, (state, action) => {
        state.accessLevel = action.payload.access_level;
        state.teamLogo = action.payload.logo;
        state.teamUrl = action.payload.team_url;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAdditionalTeamInfo.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAdditionalTeamInfo.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.invites = [...state.invites, action.payload];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(inviteUser.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(referUser.fulfilled, (state, action) => {
        state.referrals = [...state.referrals, action.payload];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(referUser.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(referUser.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(deleteInvites.fulfilled, (state, action) => {
        state.invites = state.invites.filter((invite) => !action.payload.includes(invite.invite_code));
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteInvites.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteInvites.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(deleteUsersFromOrganization.fulfilled, (state, action) => {
        const { userIds } = action.payload;

        state.teammates = state.teammates.filter((teammate) => {
          return !userIds.includes(teammate.id);
        });
        state.totalNumOfUsers -= userIds.length;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteUsersFromOrganization.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteUsersFromOrganization.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getAccountInfo.fulfilled, (state, action) => {
        state.teams = action.payload.organizations;
        state.invites = action.payload.invites;
        if (action.payload.active_organization?.id) {
          state.activeTeam = action.payload.active_organization;
        }
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAccountInfo.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAccountInfo.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(getJoinableTeamUsers.fulfilled, (state, action) => {
        // TODO: (ns) leaving the below in there, cuz i'll try to make this work separately
        // state.teammates = { [action.meta?.arg?.nameParam]: action.payload };
        state.teammates = action.payload;
        state.isError = false;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getJoinableTeamUsers.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getJoinableTeamUsers.rejected, (state) => {
        state.isError = true;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getTeamUsers.fulfilled, (state, action) => {
        // TODO: (ns) leaving the below in there, cuz i'll try to make this work separately
        state.teammates = action.payload?.teammates.results;
        state.totalNumOfUsers = action.payload?.teammates.count;
        state.organizationId = action.payload?.organizationId;
        state.numOfUsersExcludingScribe = action.payload?.teammates.users_total_excluding_scribe_users;
        state.nextUrl = action.payload?.teammates.next;
        state.previousUrl = action.payload?.previous;
        state.isError = false;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getTeamUsers.pending, (state, action) => {
        if (!action.meta.arg?.keepPreviousData) {
          state.teammates = [];
        }
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getTeamUsers.rejected, (state) => {
        state.isError = true;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getNextTeamUsers.fulfilled, (state, action) => {
        // TODO: (ns) leaving the below in there, cuz i'll try to make this work separately
        if (action.payload?.results) {
          state.teammates = [...state.teammates, ...action.payload?.results];
        }
        state.nextUrl = action.payload?.next;
        state.previousUrl = action.payload?.previous;
        state.isError = false;
        state.isLoadingNext = false;
        state.errorMessage = null;
      })
      .addCase(getNextTeamUsers.pending, (state) => {
        state.isLoadingNext = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getNextTeamUsers.rejected, (state) => {
        state.isError = true;
        state.isLoadingNext = false;
        state.errorMessage = null;
      })
      .addCase(getTeamInvites.fulfilled, (state, action) => {
        state.invites = action.payload;
        state.isError = false;
        state.errorMessage = null;
        state.loadingTeamInvites = false;
      })
      .addCase(getTeamInvites.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
        state.loadingTeamInvites = true;
      })
      .addCase(getTeamInvites.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
        state.loadingTeamInvites = false;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        const updatedTeams = [...state.teams, action.payload];

        state.teams = updatedTeams;
        if (updatedTeams.length === 1) {
          state.activeTeam = updatedTeams[0];
        }
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createTeam.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createTeam.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(editTeam.fulfilled, (state, action) => {
        state.teams = state.teams.map((team) => (team.id === action.payload.id ? action.payload : team));
        if (state.activeTeam?.id === action.payload.id) {
          state.activeTeam = action.payload;
        }
        state.accessLevel = action.payload.access_level;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(editTeam.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(editTeam.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        state.teams = state.teams.filter((team) => team.id !== action.payload);
        state.activeTeam = state.teams[0] || null;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteTeam.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteTeam.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(acceptInvite.fulfilled, (state, action) => {
        state.allInvites = state.allInvites.filter((invite) => invite.invite_code !== action.meta.arg.inviteCode);
        state.teams = [...state.teams, action.payload];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(acceptInvite.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(acceptInvite.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(rejectInvite.fulfilled, (state, action) => {
        state.allInvites = state.allInvites.filter((invite) => invite.invite_code !== action.meta.arg.inviteCode);
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(rejectInvite.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(rejectInvite.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(leaveTeam.fulfilled, (state, action) => {
        state.teams.splice(
          state.teams.findIndex((e) => e.id === action.payload[1].id),
          1
        );
        state.activeTeam = state.teams[0];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(leaveTeam.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(leaveTeam.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(requestAccess.fulfilled, (state, action) => {
        if (action.payload?.is_request) {
          state.userRequests.push(action.payload);
        } else {
          state.teams = [...state.teams, action.payload];
          if (!state.activeTeam) {
            state.activeTeam = state.teams[0];
          }
        }
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(requestAccess.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(requestAccess.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(listAccessRequests.fulfilled, (state, action) => {
        state.outsideRequests = action.payload;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listAccessRequests.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listAccessRequests.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(changeUserPermissionLevel.fulfilled, (state, action) => {
        const userOrgPermission = action.payload;

        if (!userOrgPermission) {
          return;
        }

        state.teammates = updatedUserPermissionLevel(userOrgPermission, state.teammates);
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(changeUserPermissionLevel.pending, (state) => {
        state.isError = false;
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(changeUserPermissionLevel.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.errorMessage = action?.payload?.detail || null;
      })
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.teams = state.teams.map((team) => (team.id === action.payload.id ? action.payload : team));
        if (state.activeTeam.id === action.payload.id) {
          state.activeTeam = action.payload;
        }
        state.defaultCommentEnabledForEditor = action.payload.default_comment_enabled_for_editor;
        state.defaultCommentEnabledForViewer = action.payload.default_comment_enabled_for_viewer;
        state.showUpdateReminder = action.payload.show_update_reminder;
        state.updateReminderInterval = action.payload.update_reminder_interval;
        state.showMadeWithScribe = action.payload.show_made_with_scribe;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateSetting.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateSetting.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(acknowledgeInvite.fulfilled, (state, action) => {
        state.allInvites = state.allInvites.map((invite) =>
          invite.invite_code === action.payload.invite_code ? { ...invite, acknowledged: true } : invite
        );
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(acknowledgeInvite.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(acknowledgeInvite.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(resendInvite.fulfilled, (state, action) => {
        state.invites = state.invites.map((invite) => (invite.invite_code === action.payload.invite_code ? action.payload : invite));
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(resendInvite.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(resendInvite.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(bulkResendInvites.fulfilled, (state, action) => {
        state.invites = state.invites.map((invite) =>
          action.payload.some((returnedInvite) => returnedInvite.invite_code === invite.invite_code)
            ? action.payload.find((returnedInvite) => returnedInvite.invite_code === invite.invite_code)
            : invite
        );
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(bulkResendInvites.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(bulkResendInvites.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(resendAllInvitesForTeam.fulfilled, (state, action) => {
        state.invites = state.invites.map((invite) =>
          action.payload.some((returnedInvite) => returnedInvite.invite_code === invite.invite_code)
            ? action.payload.find((returnedInvite) => returnedInvite.invite_code === invite.invite_code)
            : invite
        );
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(resendAllInvitesForTeam.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(resendAllInvitesForTeam.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(listOrganizationSettings.fulfilled, (state, action) => {
        state.defaultCommentEnabledForEditor = action.payload.default_comment_enabled_for_editor;
        state.defaultCommentEnabledForViewer = action.payload.default_comment_enabled_for_viewer;
        state.showUpdateReminder = action.payload.show_update_reminder;
        state.updateReminderInterval = action.payload.update_reminder_interval;
        state.showMadeWithScribe = action.payload.show_made_with_scribe;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listOrganizationSettings.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(listOrganizationSettings.rejected, (state) => {
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

/**
 * @typedef {Object} SelectTeamsSubState
 * @property {string} organizationId
 * @property {Array<import('scribe-web-shared/types/organization').Organization>} teams
 * @property {Array<any>} userRequests
 * @property {import('scribe-web-shared/types/organization').Organization} activeTeam
 * @property {string} errorMessage
 */

export const selectTeams = createSelector(
  [
    (state) => state.team.organizationId,
    (state) => state.team.teams,
    (state) => state.team.userRequests,
    (state) => state.team.activeTeam,
    (state) => state.team.errorMessage
  ],
  /**
   * @returns {SelectTeamsSubState}
   */
  (organizationId, teams, userRequests, activeTeam, errorMessage) => ({ organizationId, teams, userRequests, activeTeam, errorMessage })
);

export const selectActiveTeamAndWritableTeams = createSelector(
  [(state) => state.account?.data, (state) => state.team?.activeTeam, (state) => state?.team?.teams],
  (account, activeTeam, teams) => {
    const permissionLevelInActiveTeam = getPermissionLevelInTeam(account, activeTeam);
    const canWriteInActiveTeam = permissionLevelInActiveTeam && permissionLevelInActiveTeam !== 'read';

    return {
      writableTeams: (teams || []).filter((team) => team?.permission_level && team?.permission_level !== 'read'),
      activeTeam,
      canWriteInActiveTeam
    };
  }
);

export const selectAllInvites = (state) => state.team.allInvites;

export const selectTeamsAndInvites = createSelector(
  [(state) => state.team.teams, (state) => state.team.allInvites, (state) => state.team.activeTeam],
  (teams, invites, activeTeam) => ({ teams: teams.filter((team) => !team.invite_code), invites, activeTeam })
);

export const selectSuperOrganization = (state) => state.team.activeTeam?.super_organization;

export const selectIsAdmin = createSelector(
  [(state) => state.team.teams, (state) => state.team.activeTeam],
  (allTeams, activeTeam) => allTeams.find((t) => t.id === activeTeam.id && t.permission_level === 'admin') !== undefined
);

export const selectAllAdminTeams = createSelector([(state) => state.team.teams], (allTeams) =>
  (allTeams || []).filter((t) => t.permission_level === 'admin')
);

/**
 * @typedef {Object} TeammateState
 * @property {Array<import('scribe-web-shared/types/user').SharingDocumentUser>} teammates
 * @property {Array<import('v2/types').InvitedUser>} invites
 * @property {Object} activeTeam
 * @property {boolean} isLoading
 * @property {boolean} isLoadingNext
 * @property {string} selectedTeamId
 * @property {string} nextUrl
 * @property {string} previousUrl
 * @property {number} totalNumOfUsers
 * @property {number} numOfUsersExcludingScribe
 */

/**
 * @typedef {Object} RootState
 * @property {TeammateState} team
 */

/**
 * @type {function(RootState): TeammateState}
 * @property {string} accessLevel
 * @property {string} teamLogo
 * @property {string} teamUrl
 */
export const selectTeammates = createSelector(
  [
    (state) => state.team.teammates,
    (state) => state.team.invites,
    (state) => state.team.activeTeam,
    (state) => state.team.isLoading,
    (state) => state.team.isLoadingNext,
    (state) => state.team.selectedTeamId,
    (state) => state.team.nextUrl,
    (state) => state.team.previousUrl,
    (state) => state.team.totalNumOfUsers,
    (state) => state.team.numOfUsersExcludingScribe
  ],
  (
    teammates,
    invites,
    activeTeam,
    isLoading,
    isLoadingNext,
    selectedTeamId,
    nextUrl,
    previousUrl,
    totalNumOfUsers,
    numOfUsersExcludingScribe
  ) => ({
    teammates,
    invites,
    activeTeam,
    isLoading,
    isLoadingNext,
    selectedTeamId,
    nextUrl,
    previousUrl,
    totalNumOfUsers,
    numOfUsersExcludingScribe
  })
);

export const { setSelectedTeamId } = teamSlice.actions;

export default teamSlice.reducer;
